import React, { useEffect, useState, useMemo } from "react";
import styles from '../styles.module.scss';
import { FormControlLabel, Radio, RadioGroup, Typography, withStyles } from "@material-ui/core";
import Button from "../../../../../../../components/Buttons/Button";
import Checkbox from "../../../../../../../components/Inputs/Checkbox/Checkbox";
import TextInput from "../../../../../../../components/Inputs/TextInput";
import { useParams } from 'react-router-dom';
import { doGetSupportedAccessTypes } from "../../../../../../../redux/thunks/controllers";
import { useDispatch } from "react-redux";
import { allCredTypes, credsTypesMap } from "../../../../../../../utils/Common";
import { calculateWiegandParityValue } from "../../../../../../../utils/wiegandParity";
import CredentialLoaders from "../SkeletonLoaders/CredentialLoaders";
import FlexRow from "../../../../../../../components/FlexBox/FlexRow";
import SButton from "../../../../../../../components/SButton/SButton";
import { colors } from "../../../../../../../styles/colors";
import { doCreateNotification } from "../../../../../../../redux/actionCreators/miscellaneous";
import FlexColumn from "../../../../../../../components/FlexBox/FlexColumn";
import Dropdown from "../../../../../../../components/Inputs/Dropdown";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import SDialog from "../../../../../../../components/SDialog/SDialog";

const StyledRadio = withStyles({
    root: {
        color: '#BDC7D9',
        '&$checked': {
            color: '#03BFFF',
        },
    },
    checked: {},
})((props) => <Radio color="default" {...props} />);


//MODE = 0 (Add), 1 (Edit)
const CredentialDetails = ({ hNext, hBack, hSave, accessPointDetails, setAccessPointDetails, nextStep, mode = 0, hUpdate, renderTitle = true, apiInProgress = false }) => {
    const { customerId } = useParams();
    const dispatch = useDispatch();
    const [credTypes, setCredTypes] = useState([]);
    const [comProtocal, setComProtocal] = useState('1');
    const [checkedCredTypes, setCheckedCredTypes] = useState({ ...credsTypesMap });
    const [commonCredentials, setCommonCredentials] = useState({ value: "", error: "" });
    const [checkedCommonCred, setCheckedCommonCred] = useState({ ...credsTypesMap, count: 0 });
    const [checkedCommonCredPrev, setCheckedCommonCredPrev] = useState({ ...credsTypesMap });
    const [supportedAccessType, setSupportedAccessType] = useState(0);
    const [disabled, setDisabled] = useState(true);
    const [inProgress, setInProgress] = useState(true);
    const [credentialNotSupported, setCredentialNotSupported] = useState(false);
    const [commonCredentialNotSupported, setCommonCredentialNotSupported] = useState(false);
    const [wireProtocolNotSupported, setWireProtocolNotSupported] = useState(false);
    const [supportedWireProtocols, setSupportedWireProtocols] = useState({
        weigand: true,
        osdp: true,
        smartConnect: true
    })
    const [commonCredentialMode, setCommonCredentialMode] = useState("1");
    const [commonCredentialFormat, setCommonCredentialFormat] = useState("");
    const [commonCredentialType, setCommonCredentialType] = useState("1");
    const [cardNumber, setCardNumber] = useState({ value: "", error: "" });
    const [facilityCode, setFacilityCode] = useState({ value: "", error: "" });
    const [credentialChangeDialog, setCredentialChangeDialog] = useState(false);

    const calculatedParityValue = useMemo(() => {
        return calculateWiegandParityValue({
            format: commonCredentialFormat,
            type: commonCredentialType,
            credentialValue: commonCredentials?.value,
            cardNumber: cardNumber?.value,
            facilityCode: facilityCode?.value
        });
    }, [commonCredentialFormat, commonCredentialType, commonCredentials?.value, cardNumber?.value, facilityCode?.value]);

    useEffect(() => {
        if (mode === 1) {
            setInProgress(false)
            const toSend = {
                comProtocal,
                checkedCredTypes,
                commonCredentials: commonCredentials,
                commonCredentialMode: commonCredentialMode,
                commonCredentialFormat: commonCredentialFormat,
                commonCredentialType: commonCredentialType,
                cardNumber: cardNumber,
                facilityCode: facilityCode,
                checkedCommonCred,
                supportedAccessType
            }
            hUpdate && hUpdate(toSend);
        }
    }, [comProtocal, checkedCredTypes, commonCredentials, commonCredentialMode, commonCredentialFormat, commonCredentialType, cardNumber, facilityCode, checkedCommonCred, hUpdate, mode, supportedAccessType]);


    useEffect(() => {
        if (mode === 0) {
            let entryDeviceValue = accessPointDetails?.entryDeviceSerialNum?.value;
            if (["2", "3", "5"]?.includes(accessPointDetails?.controllerType) && accessPointDetails?.configuration?.value !== 8) {
                const firstCheckedChannel = Object?.values(accessPointDetails?.channelDetails)?.find(channel => channel?.checked);
                entryDeviceValue = firstCheckedChannel?.entryDeviceSerialNum?.value;
            }
            if (entryDeviceValue && accessPointDetails?.prevEntrySerialNum !== entryDeviceValue) {
                setInProgress(true);
                dispatch(doGetSupportedAccessTypes(
                    customerId,
                    entryDeviceValue,
                    (data) => {
                        if (data?.message?.isUseWithControllerSupported === false && accessPointDetails?.withController && accessPointDetails?.controllerType === "4") {
                            dispatch(doCreateNotification({ isVisible: true, message: 'This devices cannot be used with the controller' }));
                            hBack && hBack(nextStep - 2)
                        }
                        if (data?.message?.showCredentialTypes === false) {
                            setCredentialNotSupported(true)
                        }
                        if (data?.message?.showCommonCredentials === false) {
                            setCommonCredentialNotSupported(true)
                        }
                        if (data?.message?.showWireProtocol === false) {
                            setWireProtocolNotSupported(true)
                        }
                        if (data?.message?.wireProtocols) {
                            setSupportedWireProtocols(prev => ({ ...prev, ...data?.message?.wireProtocols }))
                        }
                        setSupportedAccessType(data?.message?.supportedAccessType);
                        setInProgress(false)
                    },
                    () => { hBack && hBack(nextStep - 2) }
                ));
            } else {
                setInProgress(false);
            }

            if (['1', '2', '5']?.includes(accessPointDetails?.controllerType)) {
                setComProtocal("3")
            } else if (accessPointDetails?.controllerType === "4") {
                setComProtocal("2")
            }
        }
    }, [accessPointDetails?.entryDeviceSerialNum?.value, accessPointDetails?.prevEntrySerialNum, accessPointDetails?.controllerType, accessPointDetails?.channelDetails, accessPointDetails?.configuration?.value, customerId, dispatch, hBack, mode, nextStep])

    useEffect(() => {
        if (accessPointDetails?.withController &&
            accessPointDetails?.controllerType === '4' &&
            checkedCommonCred?.count > 0 &&
            ((commonCredentialType === "1" && (commonCredentials?.value?.trim() === '' || commonCredentials?.error)) ||
                (commonCredentialType === "2" && (cardNumber?.value?.trim() === '' || facilityCode?.value?.trim() === '' || cardNumber?.error || facilityCode?.error))
            )
        ) {
            setDisabled(true)
        } else {
            setDisabled(false)
        }
    }, [accessPointDetails?.withController, accessPointDetails?.controllerType, checkedCommonCred?.count, commonCredentials, commonCredentialType, cardNumber, facilityCode])

    useEffect(() => {
        if (accessPointDetails?.comProtocal) {
            setComProtocal(accessPointDetails?.comProtocal)
        }
        if (accessPointDetails?.checkedCredTypes) {
            setCheckedCredTypes(accessPointDetails?.checkedCredTypes)
        }

        if (accessPointDetails?.site?.commonCredentailEnable) {
            setCommonCredentialFormat(accessPointDetails?.site?.commonCredentialsBitSize ? `${accessPointDetails?.site?.commonCredentialsBitSize} bits` : "");
            setCommonCredentials(accessPointDetails?.site?.commonCredentialsValue ? { value: String(accessPointDetails?.site?.commonCredentialsValue), error: "" } : { value: "", error: "" })
            setCardNumber({ value: accessPointDetails?.site?.cardNumber ? String(accessPointDetails?.site?.cardNumber) : "", error: "" });
            setFacilityCode({ value: accessPointDetails?.site?.facilityCode ? String(accessPointDetails?.site?.facilityCode) : "", error: "" });
            setCommonCredentialType(accessPointDetails?.site?.commonCredentialsValue ? "1" : "2");
        }

        if (accessPointDetails?.commonCredentials) {
            setCommonCredentials({ value: accessPointDetails?.commonCredentials || "", error: "" })
        }

        if (accessPointDetails?.commonCredentialBitSize) {
            setCommonCredentialFormat(accessPointDetails?.commonCredentialBitSize || "");
        }

        if (accessPointDetails?.cardNumber && accessPointDetails?.facilityCode) {
            setCommonCredentialType("2")
        }

        if (accessPointDetails?.cardNumber) {
            setCardNumber({ value: String(accessPointDetails?.cardNumber), error: "" });
        }

        if (accessPointDetails?.facilityCode) {
            setFacilityCode({ value: String(accessPointDetails?.facilityCode), error: "" });
        }

        if (accessPointDetails?.checkedCommonCred) {
            setCheckedCommonCred(accessPointDetails?.checkedCommonCred)
            setCheckedCommonCredPrev(accessPointDetails?.checkedCommonCred)
        }
        if (accessPointDetails?.credTypes) {
            setCredTypes(accessPointDetails?.credTypes)
        }
        setSupportedAccessType(accessPointDetails?.supportedAccessType);
        setWireProtocolNotSupported(accessPointDetails?.wireProtocolNotSupported === false ? false : true);

        if (mode === 1) {
            setCredentialNotSupported(accessPointDetails?.credentialNotSupported === false ? false : true);
            setCredentialNotSupported(!accessPointDetails?.showCredentialTypes);
            setCommonCredentialNotSupported(!accessPointDetails?.showCommonCredentials);
        }
    }, [accessPointDetails, mode])

    useEffect(() => {
        if (supportedAccessType) {
            let bits = Number(supportedAccessType)?.toString(2);
            if (bits?.length > 3) {
                bits = bits.slice(0, -5) + bits.slice(-3)
            }
            const validCredTypes = []
            const updateCheckedCreds = mode === 0 && accessPointDetails?.supportedAccessType !== supportedAccessType;
            for (let i = 0; i < allCredTypes?.length; i++) {
                if (bits[bits?.length - 1 - i] === "1") {
                    validCredTypes?.push(allCredTypes[i]);
                    updateCheckedCreds && setCheckedCredTypes(prev => ({ ...prev, [allCredTypes[i]?.id]: true }))
                } else {
                    updateCheckedCreds && setCheckedCredTypes(prev => ({ ...prev, [allCredTypes[i]?.id]: false }))
                }
            }
            setCredTypes(validCredTypes);
        }
    }, [supportedAccessType, accessPointDetails?.supportedAccessType, mode])

    const handleChange = (event) => {
        setComProtocal(event.target.value);
    };

    const handleNext = () => {
        const toSend = {
            comProtocal,
            checkedCredTypes,
            commonCredentials: commonCredentials?.value,
            commonCredentialMode: commonCredentialMode,
            commonCredentialFormat: commonCredentialFormat,
            commonCredentialType: commonCredentialType,
            cardNumber: cardNumber?.value,
            facilityCode: facilityCode?.value,
            checkedCommonCred,
            supportedAccessType,
            credTypes,
            prevEntrySerialNum: accessPointDetails?.entryDeviceSerialNum?.value,
            credentialNotSupported,
            wireProtocolNotSupported,
        }
        hNext && hNext(toSend, nextStep || 4);
    }

    const handleSave = () => {
        const toSend = {
            comProtocal,
            checkedCredTypes,
            commonCredentials: commonCredentials?.value,
            commonCredentialMode: commonCredentialMode,
            commonCredentialFormat: commonCredentialFormat,
            commonCredentialType: commonCredentialType,
            changeForAllAccessPoints: true,
            cardNumber: cardNumber?.value,
            facilityCode: facilityCode?.value,
            checkedCommonCred,
            supportedAccessType
        }
        hSave && hSave(toSend);
    }

    const hToggleCredTypes = (name, value, isCommonCred) => {
        if (isCommonCred) {
            setCheckedCommonCred(prev => ({
                ...prev,
                [name]: value,
                count: value ? checkedCommonCred?.count + 1 : checkedCommonCred?.count - 1
            }));
            setCheckedCommonCredPrev(prev => ({
                ...prev,
                [name]: value,
            }));
        } else {
            if (value === false) {
                setCheckedCommonCred(prev => ({
                    ...prev,
                    [name]: value,
                    count: checkedCommonCred?.count - 1
                }));
            } else {
                setCheckedCommonCred(prev => ({
                    ...prev,
                    [name]: checkedCommonCredPrev[name],
                    count: checkedCommonCredPrev[name] ? checkedCommonCred?.count + 1 : checkedCommonCred?.count - 1
                }));
            }
            setCheckedCredTypes(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }

    const commonCredentialChanged = () => {
        const siteCreds = accessPointDetails?.site;
        if (!siteCreds?.commonCredentailEnable || checkedCommonCred?.count <= 0) {
            return false;
        }

        if (siteCreds?.commonCredentialsBitSize !== Number(commonCredentialFormat?.slice(0, 2))) {
            return true;
        }

        if (commonCredentialType === "1" && Number(siteCreds?.commonCredentialsValue) !== Number(commonCredentials?.value)) {
            return true;
        }

        if (commonCredentialType === "2" && (Number(siteCreds?.facilityCode) !== Number(facilityCode?.value) || Number(siteCreds?.cardNumber) !== Number(cardNumber?.value))) {
            return true;
        }

        return false;
    }


    return (<>
        <FlexColumn alignItems="start" gap="0" styles={{ marginBottom: '5rem', overflowX: 'hidden' }}>
            <div className={styles['column']} style={{ width: '100%' }}>
                <div className={styles['row']} style={{ gap: '44%' }}>
                    {renderTitle && <h2 className={styles['bold-text']} style={{ fontWeight: '500', color: '#4766B7' }}>Credential types</h2>}
                    {accessPointDetails?.withController && accessPointDetails?.controllerType === '4' && <h2 className={styles['bold-text']} style={{ fontWeight: '500', color: '#4766B7' }}>Common credential</h2>}
                </div>
                {accessPointDetails?.configuration?.value === 9 && <Typography variant="b1SB" style={{ color: colors.error500 }}>Elevators and lockers currently support Card, QR and Mobile Access only. Other access types will be available in a future phase.</Typography>}
                {(accessPointDetails?.withController && accessPointDetails?.controllerType === '4') ?
                    <div className={styles['column']}>
                        {credTypes?.map(cType => {
                            return (
                                <>
                                    <div className={styles['row']} style={{ gap: '40%' }}>
                                        <div className={styles['checkbox_container']} style={{ marginBottom: '0' }}>
                                            <Checkbox
                                                disabled={credentialNotSupported}
                                                checked={checkedCredTypes[cType?.id]}
                                                onChange={() => {
                                                    hToggleCredTypes(cType?.id, !checkedCredTypes[cType?.id])
                                                }}
                                                name={cType?.id}
                                            />
                                            <p className={styles['access_text']}>{cType?.name}</p>
                                        </div>
                                        <div className={styles['checkbox_container']} style={{ marginBottom: '0' }}>
                                            <Checkbox
                                                disabled={!checkedCredTypes[cType?.id] || commonCredentialNotSupported}
                                                checked={checkedCommonCred[cType?.id]}
                                                onChange={() => {
                                                    hToggleCredTypes(cType?.id, !checkedCommonCred[cType?.id], true)
                                                }}
                                                name={cType?.id}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles['divider']} style={{ margin: '0' }} />
                                </>
                            )
                        })}
                    </div>
                    : <div className={styles['row']} style={{ flexWrap: 'wrap', marginTop: renderTitle ? '1.5rem' : '0', justifyContent: 'space-between', width: '100%' }}>
                        {
                            inProgress ? <CredentialLoaders /> :
                                credTypes?.map(cType => {
                                    let forceUncheck = false;
                                    if (accessPointDetails?.configuration?.value === 9 && !['hf_cards', 'qr_code', 'mobile_ble', 'mobile_nfc']?.includes(cType?.id)) {
                                        forceUncheck = true
                                    }
                                    return <div className={styles['checkbox_container']}>
                                        <Checkbox
                                            disabled={credentialNotSupported}
                                            checked={forceUncheck ? false : checkedCredTypes[cType?.id]}
                                            onChange={() => {
                                                hToggleCredTypes(cType?.id, !checkedCredTypes[cType?.id])
                                            }}
                                            name={cType?.id}
                                        />
                                        <p className={styles['access_text']}>{cType?.name}</p>
                                    </div>
                                })
                        }
                    </div>}
            </div>

            {accessPointDetails?.withController && !['2', '5']?.includes(accessPointDetails?.controllerType) && comProtocal !== '1' && <div className={styles['column']}>
                <h2 className={styles['bold-text']} style={{ fontWeight: '500', marginBottom: '1.5rem', marginTop: '2rem', color: '#4766B7' }}>Communication Protocol</h2>
                <RadioGroup aria-label="Communication-Protocol" name="communicationProtocol" value={comProtocal} onChange={handleChange}>
                    {!['1', '2', '5']?.includes(accessPointDetails?.controllerType) && <FormControlLabel disabled={accessPointDetails?.controllerType === "4" || wireProtocolNotSupported} value="4" control={<StyledRadio />} label={`Smart connect ${accessPointDetails?.controllerType === "4" ? '(Coming soon)' : ''}`} />}
                    <FormControlLabel disabled={accessPointDetails?.controllerType === "4" || wireProtocolNotSupported} value="3" control={<StyledRadio />} label={`OSDP ${accessPointDetails?.controllerType === "4" ? '(Coming soon)' : ''}`} />
                    {!['1', '2', '5']?.includes(accessPointDetails?.controllerType) && <FormControlLabel value="2" disabled={wireProtocolNotSupported} control={<StyledRadio />} label="Weigand" />}
                </RadioGroup>
            </div>}

            {accessPointDetails?.withController && accessPointDetails?.controllerType === '4' && checkedCommonCred?.count > 0 &&
                <FlexColumn gap="0.5rem" alignItems="start">
                    <h2 className={styles['bold-text']} style={{ fontWeight: '500', marginTop: '2rem', color: '#4766B7' }}>Common credential</h2>
                    <RadioGroup aria-label="Common-credential-Mode" name="commonCredentialMode" value={commonCredentialMode} onChange={(e) => setCommonCredentialMode(e.target.value)} row>
                        <FormControlLabel value="1" control={<StyledRadio />} label="Manual" />
                        <FormControlLabel value="2" control={<StyledRadio />} label="Auto Generate" />
                    </RadioGroup>

                    <Dropdown
                        placeholder="Select Format"
                        value={commonCredentialFormat}
                        onChange={(data) => {
                            if (data === '32 bits') {
                                setCommonCredentialType("1")
                            }
                            setCommonCredentialFormat(data);
                            setCommonCredentials({ value: "", error: "" });
                            setCardNumber({ value: "", error: "" });
                            setFacilityCode({ value: "", error: "" });
                        }}
                        data={["26 bits", "32 bits", "48 bits"]}
                    />

                    {commonCredentialFormat &&
                        <>
                            {(commonCredentialMode === "2" && commonCredentialType === "1") && (
                                <SButton
                                    onClick={() => {
                                        let maxValue = 0;
                                        if (commonCredentialFormat === "26 bits") {
                                            maxValue = 16777215;
                                        } else if (commonCredentialFormat === "32 bits") {
                                            maxValue = 4294967295;
                                        } else if (commonCredentialFormat === "48 bits") {
                                            maxValue = 35184372088831;
                                        }
                                        const value = Math.floor(Math.random() * maxValue);
                                        setCommonCredentials({ value: String(value), error: "" })
                                    }}
                                >
                                    <ArrowsClockwiseIcon size={18} style={{ marginRight: '0.5rem' }} /> Generate Common Credential</SButton>
                            )}

                            <RadioGroup aria-label="Common-credential-Type" name="commonCredentialType" value={commonCredentialType} onChange={(e) => setCommonCredentialType(e.target.value)} row>
                                <FormControlLabel value="1" control={<StyledRadio />} label="Credential ID" />
                                {commonCredentialFormat !== "32 bits" && <FormControlLabel value="2" control={<StyledRadio />} label="Card Number & Facility Code" />}
                            </RadioGroup>

                            {commonCredentialType === "1" && (
                                <FlexColumn gap="0" alignItems="start" style={{ width: '100%' }}>
                                    <TextInput
                                        name=""
                                        placeholder="Common credential Value"
                                        value={commonCredentials?.value}
                                        onChange={(e) => {
                                            const value = e?.target?.value;
                                            if (Number(value) < 0) {
                                                return;
                                            }
                                            let maxValue = 0;
                                            if (commonCredentialFormat === "26 bits") {
                                                maxValue = 16777215;
                                            } else if (commonCredentialFormat === "32 bits") {
                                                maxValue = 4294967295;
                                            } else if (commonCredentialFormat === "48 bits") {
                                                maxValue = 35184372088831;
                                            }

                                            setCommonCredentials({ value, error: Number(value) > maxValue ? `Value cannot exceed ${maxValue} for the selected format` : "" })
                                        }}
                                        type="number"
                                        error={commonCredentials?.error}
                                    />
                                    {calculatedParityValue !== null && (
                                        <Typography variant="b3M" style={{ color: '#4766B7', marginTop: '0.25rem' }}>
                                            Value with parity: {calculatedParityValue}
                                        </Typography>
                                    )}
                                </FlexColumn>
                            )}

                            {commonCredentialType === "2" && <FlexColumn gap="1rem" alignItems="start" style={{ width: '100%' }}>
                                <FlexColumn gap="0" alignItems="start" style={{ width: '100%' }}>
                                    <TextInput
                                        name=""
                                        placeholder="Card Number"
                                        value={cardNumber?.value}
                                        onChange={(e) => {
                                            const value = e?.target?.value;
                                            if (Number(value) < 0) {
                                                return;
                                            }
                                            let maxValue = 0;
                                            if (commonCredentialFormat === "26 bits") {
                                                maxValue = 65535;
                                            } else if (commonCredentialFormat === "48 bits") {
                                                maxValue = 8388607;
                                            }

                                            setCardNumber({ value, error: Number(value) > maxValue ? `Value cannot exceed ${maxValue} for the selected format` : "" })
                                        }}
                                        type="number"
                                        error={cardNumber?.error}
                                    />
                                </FlexColumn>
                                <FlexColumn gap="0" alignItems="start" style={{ width: '100%' }}>
                                    <TextInput
                                        name=""
                                        placeholder="Facility Code"
                                        value={facilityCode?.value}
                                        onChange={(e) => {
                                            const value = e?.target?.value;
                                            if (Number(value) < 0) {
                                                return;
                                            }
                                            let maxValue = 0;
                                            if (commonCredentialFormat === "26 bits") {
                                                maxValue = 255;
                                            } else if (commonCredentialFormat === "48 bits") {
                                                maxValue = 4194303;
                                            }

                                            setFacilityCode({ value, error: Number(value) > maxValue ? `Value cannot exceed ${maxValue} for the selected format` : "" })
                                        }}
                                        type="number"
                                        error={facilityCode?.error}
                                    />
                                    {calculatedParityValue !== null && (
                                        <Typography variant="b3M" style={{ color: '#4766B7', marginTop: '0.25rem' }}>
                                            Value with parity: {calculatedParityValue}
                                        </Typography>
                                    )}
                                </FlexColumn>
                            </FlexColumn>}
                        </>
                    }
                </FlexColumn>}
        </FlexColumn>

        {mode === 0 &&
            <FlexRow styles={{ marginTop: '1rem' }} justifyContent="end" className={styles["fixed-actions"]}>
                <SButton disabled={apiInProgress} color={"grey"} minWidth onClick={() => { hBack && hBack(nextStep - 2) }}>Back</SButton>
                <SButton
                    disabled={disabled || inProgress || apiInProgress}
                    minWidth
                    onClick={() => {
                        if (accessPointDetails?.controllerType === "4") {
                            const siteCreds = accessPointDetails?.site;
                            if (commonCredentialChanged()) {
                                setCredentialChangeDialog(true);
                            } else {
                                handleSave();
                            }
                        } else {
                            handleNext();
                        }
                    }}>
                    {accessPointDetails?.controllerType === "4" ? 'Add' : 'Next'}
                </SButton>
            </FlexRow>
        }

        <SDialog
            open={credentialChangeDialog}
            onClose={() => { setCredentialChangeDialog(false); }}
            title="Common credentials changed"
            primaryButtonConfig={{
                text: 'Okay',
                onClick: () => {
                    handleSave();
                    setCredentialChangeDialog(false);
                }
            }}

        >
            <Typography variant="b1SB" style={{ color: colors.error500 }}>Common credentials have been changed. This will change the credential values for all existing device and at organisation level</Typography>
        </SDialog>

    </>)
};

export default CredentialDetails;